import m from 'moment';
import * as Yup from 'yup';
import { LMC_ACCEPTED_APPLICANT_AGE_START } from 'shared/interface/globalInterface';
import { Nullable } from 'utils/types';
import { APPLICANT_PREFIXES } from './types';

const EMPTY_STRING_SEPERATOR = ' ';
const DATE_TIME_FORMAT = 'YYYY-MM-DD hh:mm a';
export const VISIBLE_DATE_SEPARATOR = '/';
export const VISIBLE_DATE_FORMAT = 'DD' + VISIBLE_DATE_SEPARATOR + 'MM' + VISIBLE_DATE_SEPARATOR + 'YYYY';
export const REQUEST_DATE_FORMAT = 'DD/MM/YYYY';
export const BACKEND_DATE_FORMAT = 'YYYY-MM-DD';
export const ISO_DATE_FORMAT = '';
export const DATE_TIME_FORMAT_WITH_HOURS_AND_MINUTES = VISIBLE_DATE_FORMAT + EMPTY_STRING_SEPERATOR + 'hh:mm';

type DateFormatter = {
  fromBackendToView: () => string;
  fromViewToRequest: (strict?: boolean) => string;
  toView: () => string;
  fromBackendToCustom: (to: string) => string;
  dateTime: (format?: string) => string;
  dataTimeWithHoursAndMinutes: () => string;
  fromBackendToViewWithHoursAndMinutes: () => string;
};

const formatDate = (date: string | Date, from: string | m.MomentBuiltinFormat, to: string, strict = false): string =>
  date ? m(date, from, Boolean(strict)).format(to) : '';

export const format = (date: string | Date): DateFormatter => {
  return {
    fromBackendToView: () => formatDate(date, BACKEND_DATE_FORMAT, VISIBLE_DATE_FORMAT),
    fromBackendToViewWithHoursAndMinutes: () => formatDate(date, m.ISO_8601, DATE_TIME_FORMAT_WITH_HOURS_AND_MINUTES),
    fromViewToRequest: (strict = false) => formatDate(date, VISIBLE_DATE_FORMAT, BACKEND_DATE_FORMAT, strict),
    toView: () => formatDate(date, VISIBLE_DATE_FORMAT, VISIBLE_DATE_FORMAT),
    fromBackendToCustom: (to: string) => formatDate(date, BACKEND_DATE_FORMAT, to),
    dateTime: (format?: string) => formatDate(date, '', format || DATE_TIME_FORMAT),
    dataTimeWithHoursAndMinutes: (format?: string) =>
      formatDate(date, '', format || DATE_TIME_FORMAT_WITH_HOURS_AND_MINUTES),
  };
};

export const hasFormat = (date: string | Date, formatPattern: string): boolean => {
  return moment(date, formatPattern).format(formatPattern) === date;
};

export const calculateDiff = (
  date: string | Date,
  unitOfTime: 'years' | 'day' | 'months' = 'years',
  precise = false,
): number => {
  const choosenDate = moment(date, VISIBLE_DATE_FORMAT);

  return m().diff(choosenDate, unitOfTime, precise);
};

export const isValid = (date: string | Date, strict: boolean = false): boolean => {
  return m(date, VISIBLE_DATE_FORMAT, strict).isValid();
};

export const moment = m;

export const dateValidation = (strict: boolean = false): Yup.StringSchema<string> =>
  Yup.string().test('checkFormat', 'The date is incorrect. Please make sure you input a correct date.', (val: any) => {
    return val ? !val.match('_') && isValid(val, strict) : true;
  });

export const pastDateValidation = (nullable: boolean = false, strict: boolean = false): Yup.StringSchema<string> =>
  dateValidation(strict).test('pastDate', `Date must be in the past`, (value: Date | string) => {
    if (nullable && (value === '' || value === null || value === undefined)) {
      return true;
    }
    return moment(value, VISIBLE_DATE_FORMAT).isBefore(new Date(), 'day');
  });

export const futureDateValidation = (strict: boolean = false): Yup.StringSchema<string> =>
  dateValidation(strict).test('futureDate', `Date must be in the future`, (value: Date) => {
    return value ? moment(value, VISIBLE_DATE_FORMAT).isAfter(new Date(), 'day') : true;
  });

export const todayAndFutureDateValidation = (strict: boolean = false): Yup.StringSchema<string> =>
  dateValidation(strict).test('futureDate', `Date must be today or in the future`, (value: Date) => {
    return value ? moment(value, VISIBLE_DATE_FORMAT).isSameOrAfter(new Date(), 'day') : true;
  });

export const applicantAgeValidation = (nullable: boolean = false): Yup.StringSchema<string> =>
  dateValidation()
    .test(
      'ageCounter',
      `We are only able to accept applications from over 50 year old's, as per FCA retirement interest only mortgage guidelines.`,
      (value: Date) => {
        if (nullable && !value) {
          return true;
        }
        const age = calculateDiff(value, 'years', true);
        return age >= LMC_ACCEPTED_APPLICANT_AGE_START;
      },
    )
    .test('ageCounter', `Maximum age we can consider is 150 years old.`, (value: Date) => {
      if (nullable && !value) {
        return true;
      }
      const age = calculateDiff(value);
      return age <= 150;
    });

export const getOlderApplicantBasedOnDateOfBirth = (
  firstApplicantDOB: string | Date,
  secondApplicantDOB: Nullable<string | Date>,
) => {
  if (!secondApplicantDOB) {
    return APPLICANT_PREFIXES.APPLICANT_1;
  }

  const firstApplicantAge = calculateDiff(firstApplicantDOB, 'years');
  const secondApplicantAge = calculateDiff(secondApplicantDOB, 'years');

  if (firstApplicantAge >= secondApplicantAge) {
    return APPLICANT_PREFIXES.APPLICANT_1;
  }

  if (secondApplicantAge > firstApplicantAge) {
    return APPLICANT_PREFIXES.APPLICANT_2;
  }

  return APPLICANT_PREFIXES.APPLICANT_1;
};
